/**
 * The plan's *other* half: `plans.route`, the ordered waypoint list the map edits
 * (see `server/api/migrations/0004_plan_route.sql` for the stored shape).
 *
 * Nothing here touches the DOM, MapLibre or the network — it is the pure mapping
 * between waypoints and LBA form fields, which is what the tests pin down. Legs
 * are never stored: `@avia/core`'s `routeLegs` derives course, distance and
 * variation from the waypoints, so the map, the navlog and the printed form
 * cannot disagree.
 */

import { distanceNm, formatVar, norm360, routeLegs, type LatLon } from '@avia/core';

export type WaypointKind = 'airport' | 'navaid' | 'reporting_point' | 'user';

/** The tile/API layers a waypoint can be snapped to. */
export type FeatureLayer = 'airports' | 'airspaces' | 'navaids' | 'reporting_points' | 'obstacles';

export interface WaypointRef {
  layer: FeatureLayer;
  id: string;
}

export interface Waypoint {
  /** unique within the plan; generated client-side */
  id: string;
  /** what the pilot sees and what lands in the cp<N> column; ≤ 40 chars */
  name: string;
  lat: number;
  lon: number;
  kind: WaypointKind;
  /** the openAIP feature this was snapped to, or null for a free point */
  ref: WaypointRef | null;
  /** planned altitude AT the waypoint, ft MSL */
  alt_ft: number | null;
  /**
   * ICAO code when the waypoint is an airport that has one. Not part of the
   * server's validated key set (it tolerates extra keys and round-trips them);
   * it exists so `von`/`nach` stay exact after the pilot renames a waypoint.
   */
  icao?: string | undefined;
}

export interface RouteDoc {
  v: 1;
  waypoints: Waypoint[];
  /**
   * The form-field ids this route currently owns. Persisted so a reload still
   * knows which cells came from the map (and which of them the pilot has since
   * overridden) — the values themselves live in `values`/`manual` as always.
   */
  synced_ids?: string[];
}

/** The LBA form has twelve route rows; a longer route is flown but not printed row-by-row. */
export const MAX_ROUTE_LEGS = 12;
/** Matches the server's cap (`plans.rs::MAX_WAYPOINTS`). */
export const MAX_WAYPOINTS = 60;
export const MAX_WAYPOINT_NAME = 40;

export const emptyRoute = (): RouteDoc => ({ v: 1, waypoints: [] });

export const hasWaypoints = (r: RouteDoc | null | undefined): boolean => !!r && r.waypoints.length > 0;

/* ---------------- parsing (the column is schemaless, we are not) ---------------- */

const isObj = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const KINDS: readonly WaypointKind[] = ['airport', 'navaid', 'reporting_point', 'user'];
const LAYERS: readonly FeatureLayer[] = ['airports', 'airspaces', 'navaids', 'reporting_points', 'obstacles'];

function parseRef(v: unknown): WaypointRef | null {
  if (!isObj(v)) return null;
  const layer = v['layer'];
  const id = v['id'];
  if (typeof layer !== 'string' || typeof id !== 'string' || !id) return null;
  return (LAYERS as readonly string[]).includes(layer) ? { layer: layer as FeatureLayer, id } : null;
}

function parseWaypoint(v: unknown, i: number): Waypoint | null {
  if (!isObj(v)) return null;
  const lat = Number(v['lat']);
  const lon = Number(v['lon']);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;
  const kindRaw = v['kind'];
  const kind: WaypointKind = (KINDS as readonly unknown[]).includes(kindRaw) ? (kindRaw as WaypointKind) : 'user';
  const id = typeof v['id'] === 'string' && v['id'] ? v['id'].slice(0, 64) : 'w' + (i + 1);
  const name = typeof v['name'] === 'string' ? v['name'].slice(0, MAX_WAYPOINT_NAME) : '';
  const altRaw = v['alt_ft'];
  const alt = typeof altRaw === 'number' && Number.isFinite(altRaw) ? Math.round(altRaw) : null;
  const icao = typeof v['icao'] === 'string' && v['icao'] ? v['icao'].toUpperCase().slice(0, 8) : undefined;
  return {
    id,
    name: name || 'WP' + (i + 1),
    lat,
    lon,
    kind,
    ref: parseRef(v['ref']),
    alt_ft: alt !== null && alt >= 0 && alt <= 60000 ? alt : null,
    ...(icao !== undefined ? { icao } : {}),
  };
}

/**
 * Read whatever the API/localStorage handed us. A document we cannot understand
 * becomes `null` rather than an exception — a broken route must never keep a
 * pilot out of the form half of the plan.
 */
export function parseRoute(raw: unknown): RouteDoc | null {
  if (!isObj(raw)) return null;
  const list = raw['waypoints'];
  if (!Array.isArray(list)) return null;
  const waypoints: Waypoint[] = [];
  for (const [i, w] of list.entries()) {
    const p = parseWaypoint(w, i);
    if (p) waypoints.push(p);
    if (waypoints.length >= MAX_WAYPOINTS) break;
  }
  const ids = raw['synced_ids'];
  const synced = Array.isArray(ids) ? ids.filter((x): x is string => typeof x === 'string') : undefined;
  return { v: 1, waypoints, ...(synced ? { synced_ids: synced } : {}) };
}

/** Waypoint ids only have to be unique inside one plan, so a counter + salt is enough. */
export function newWaypointId(): string {
  const rnd = Math.random().toString(36).slice(2, 8);
  return 'w' + Date.now().toString(36) + rnd;
}

/**
 * "WP3" for the next free number — free meaning no existing waypoint already
 * carries it, so deleting WP2 and adding again does not produce two WP3s.
 */
export function nextWaypointName(waypoints: readonly Waypoint[]): string {
  const used = new Set<number>();
  for (const w of waypoints) {
    const m = /^WP(\d+)$/.exec(w.name.trim().toUpperCase());
    if (m) used.add(Number(m[1]));
  }
  let n = waypoints.length + 1;
  // start at the count so a fresh route reads WP1, WP2, … and never reuses a live name
  if (n < 1) n = 1;
  while (used.has(n)) n++;
  return 'WP' + n;
}

/* ---------------- snapping ---------------- */

/** The attribute bag a clicked vector-tile feature carries (see the tiles contract). */
export interface SnapProps {
  id?: unknown;
  name?: unknown;
  icao?: unknown;
  ident?: unknown;
  kind?: unknown;
}

/** Tile source-layer → the waypoint kind it produces. Airspaces/obstacles never snap. */
const SNAP_KIND: Record<string, WaypointKind> = {
  airports: 'airport',
  navaids: 'navaid',
  reporting_points: 'reporting_point',
};

export const isSnappable = (layer: string): boolean => layer in SNAP_KIND;

/**
 * The name a snapped waypoint gets: the ICAO code / navaid ident when the
 * feature has one (that is what a pilot writes in the Kontrollpunkt column),
 * otherwise the published name, trimmed to what the field holds.
 */
export function snapName(layer: string, props: SnapProps): string {
  const s = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');
  const ident = layer === 'airports' ? s(props.icao) : s(props.ident);
  const name = s(props.name);
  return (ident || name || 'WP').slice(0, MAX_WAYPOINT_NAME);
}

/** Build a waypoint from a clicked feature; `null` for a layer that cannot be a waypoint. */
export function waypointFromFeature(
  layer: string,
  props: SnapProps,
  at: LatLon,
  id = newWaypointId(),
): Waypoint | null {
  const kind = SNAP_KIND[layer];
  if (!kind) return null;
  const fid = typeof props.id === 'string' ? props.id : '';
  const icao = layer === 'airports' && typeof props.icao === 'string' ? props.icao.toUpperCase() : undefined;
  return {
    id,
    name: snapName(layer, props),
    lat: at.lat,
    lon: at.lon,
    kind,
    ref: fid ? { layer: layer as FeatureLayer, id: fid } : null,
    alt_ft: null,
    ...(icao ? { icao } : {}),
  };
}

/**
 * Are these two waypoints the same place? Used to refuse an append that would produce a
 * 0 NM leg on a course of 360° — which is what `initialBearingDeg(a, a)` and `fmtDistNm(0)`
 * write into the printed navlog, and what the wind triangle then divides by. Same openAIP
 * feature counts as the same place even if the two came from different sources (the search
 * box and the info card), and so does a coordinate match to ~0.1 m.
 */
export function isSameSpot(a: Waypoint | undefined, b: Waypoint | undefined): boolean {
  if (!a || !b) return false;
  if (a.ref && b.ref) return a.ref.layer === b.ref.layer && a.ref.id === b.ref.id;
  return Math.abs(a.lat - b.lat) < 1e-6 && Math.abs(a.lon - b.lon) < 1e-6;
}

/** A free point the pilot clicked: "WP3" until they rename it. */
export function userWaypoint(waypoints: readonly Waypoint[], at: LatLon, id = newWaypointId()): Waypoint {
  return { id, name: nextWaypointName(waypoints), lat: at.lat, lon: at.lon, kind: 'user', ref: null, alt_ft: null };
}

/** One row of `GET /api/geo/search`, reduced to what a waypoint needs. */
export interface SearchHit {
  layer: string;
  id: string;
  name: string;
  ident: string | null;
  lat: number;
  lon: number;
}

/**
 * A waypoint from a search result. The API and the tiles agree on `id`
 * (the openAIP `_id`), so a waypoint added from the search box is the same
 * object as one snapped on the map — right down to the `ref` the info card
 * later looks up.
 */
export function waypointFromHit(hit: SearchHit, existing: readonly Waypoint[]): Waypoint {
  const props: SnapProps = {
    id: hit.id,
    name: hit.name,
    ...(hit.layer === 'airports' ? { icao: hit.ident ?? undefined } : { ident: hit.ident ?? undefined }),
  };
  return (
    waypointFromFeature(hit.layer, props, { lat: hit.lat, lon: hit.lon }, newWaypointId()) ??
    userWaypoint(existing, { lat: hit.lat, lon: hit.lon })
  );
}

/* ---------------- the route → Formular mapping ---------------- */

/** ICAO-shaped: four letters/digits starting with two letters (LJLJ, EDDF, LFPG). */
const ICAOISH = /^[A-Z]{2}[A-Z0-9]{2}$/;

/** The airport code of a waypoint, or '' — what `von` / `nach` may be filled with. */
export function waypointIcao(w: Waypoint | undefined): string {
  if (!w || w.kind !== 'airport') return '';
  if (w.icao && ICAOISH.test(w.icao)) return w.icao;
  const n = w.name.trim().toUpperCase();
  return ICAOISH.test(n) ? n : '';
}

/**
 * A leg distance the way the form shows it: at most one decimal, and never a
 * trailing ".0" — the paper form writes "45.6" and "46", not "46.0".
 */
export function fmtDistNm(nm: number): string {
  if (!Number.isFinite(nm)) return '';
  const r = Math.round(nm * 10) / 10;
  return Number.isInteger(r) ? String(r) : r.toFixed(1);
}

export interface RouteSync {
  /** field id → the value this route derives for it */
  values: Map<string, string>;
  /** legs actually written (≤ MAX_ROUTE_LEGS) */
  legs: number;
  /** legs the route has but the form cannot hold */
  dropped: number;
}

/**
 * Everything a route writes into the Formular.
 *
 * The form's route block is a "Start" row plus twelve legs, each ending at a
 * checkpoint: `cp1_name` is the departure and `cp(j+1)_name` the checkpoint at
 * the END of leg `j`. So `n` waypoints fill `n` cp-name cells and `n-1` leg rows.
 *
 * VAR is written on **every** leg row even though the calculator carries the last
 * one downwards — a pilot who deletes one row must not silently inherit a
 * neighbour's variation.
 */
export function routeSyncValues(route: RouteDoc | null | undefined, date: Date): RouteSync {
  const values = new Map<string, string>();
  const wps = route?.waypoints ?? [];
  if (wps.length === 0) return { values, legs: 0, dropped: 0 };

  const first = wps[0];
  if (first) values.set('cp1_name', first.name);

  const legs = routeLegs(wps, date);
  const used = Math.min(legs.length, MAX_ROUTE_LEGS);
  for (let i = 0; i < used; i++) {
    const leg = legs[i];
    const to = wps[i + 1];
    if (!leg || !to) continue;
    const j = i + 1;
    values.set('leg' + j + '_tc', String(norm360(leg.tc)));
    values.set('leg' + j + '_dist', fmtDistNm(leg.distNm));
    values.set('leg' + j + '_var', formatVar(leg.varDeg));
    values.set('cp' + (j + 1) + '_name', to.name);
  }

  const von = waypointIcao(first);
  if (von) values.set('von', von);
  // the destination is the LAST waypoint even when the form ran out of leg rows:
  // the flight ends there whatever the paper can print
  const nach = waypointIcao(wps[wps.length - 1]);
  if (nach && wps.length > 1) values.set('nach', nach);

  return { values, legs: used, dropped: Math.max(0, legs.length - used) };
}

/**
 * How long the drawn route is, in NM — the one number a flight list can show
 * without opening the plan. Measured along the same great circles the navlog and
 * the airspace check use, so the list and the form cannot disagree.
 */
export function routeTotalNm(route: RouteDoc | null | undefined): number {
  const wps = route?.waypoints ?? [];
  if (wps.length < 2) return 0;
  let sum = 0;
  for (let i = 1; i < wps.length; i++) {
    const a = wps[i - 1];
    const b = wps[i];
    if (a && b) sum += distanceNm({ lat: a.lat, lon: a.lon }, { lat: b.lat, lon: b.lon });
  }
  return sum;
}

/* ---------------- altitudes for the airspace check ---------------- */

/** The range a bare integer in the Höhe/FL column is read as a flight level. */
const BARE_FL_MIN = 10;
const BARE_FL_MAX = 99;

/**
 * "3500", "3.5" (thousands), "FL 65", "65" — what the Höhe/FL column may hold.
 *
 * A **bare two-digit number is a flight level**, not feet. That is how the LBA's
 * own worked example writes the column (`packages/core/test/
 * fixtures-example-flight.json`: `leg1_alt "65"`, `leg6_alt "60"`, against
 * `alt_alt "3.0"` for 3 000 ft), and 10–99 ft is not an altitude anybody cruises
 * at — while 100–999 ft is (a low-level leg), so three digits stay feet. Every
 * consumer of this parse depends on getting it right: the airspace check asks
 * about that level, `winds.ts` asks the forecast at it, and the W/V it writes
 * back is what WCA, MH, GS and the leg time are stamped from.
 */
export function parseAltFt(raw: string): number | null {
  const v = raw.trim().toUpperCase().replace(/,/g, '.');
  if (!v) return null;
  let m = /^FL\s*(\d{1,3})$/.exec(v);
  if (m) return Number(m[1]) * 100;
  m = /^(\d+(?:\.\d+)?)$/.exec(v);
  if (!m) return null;
  const n = Number(m[1]);
  if (!Number.isFinite(n)) return null;
  // "3.5" on this form means 3 500 ft
  if (v.includes('.')) return Math.round(n * 1000);
  // "65" / "065" is FL 65 — see above
  if (n >= BARE_FL_MIN && n <= BARE_FL_MAX) return Math.round(n) * 100;
  return Math.round(n);
}

/** The default the airspace check falls back to when neither map nor form says. */
export const DEFAULT_CHECK_ALT_FT = 3000;

/**
 * The altitude to check each waypoint at: its own `alt_ft`, else the Höhe/FL of
 * the leg that ENDS there (the departure inherits the first leg's), else the
 * default. Never invents a level below ground: it is a hint, not a clearance.
 */
export function checkAltitudes(waypoints: readonly Waypoint[], legAlt: (leg: number) => string): (number | null)[] {
  return waypoints.map((w, i) => {
    if (w.alt_ft != null) return w.alt_ft;
    const legNo = i === 0 ? 1 : i;
    if (legNo >= 1 && legNo <= MAX_ROUTE_LEGS) {
      const parsed = parseAltFt(legAlt(legNo));
      if (parsed != null) return parsed;
    }
    return null;
  });
}
